import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Collapse, Pagination, Form, Alert, Button } from "react-bootstrap";
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";

function AdminTemplateList() {
    const navigate = useNavigate();
    const [templateList, setTemplateList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [mediumTypeFilter, setMediumTypeFilter] = useState('');
    const [usedByFilter, setUsedByFilter] = useState('');

    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0
    });

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getTemplates = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Clear template list when filters change
                if (searchTerm || mediumTypeFilter || usedByFilter) {
                    setTemplateList([]);
                }

                const res = await templateService.getEcxTemplate(
                    pagination.page,
                    pagination.size,
                    { signal: controller.signal }
                );

                if (isMounted) {
                    setTemplateList(res.data.content);
                    setPagination(prev => ({
                        ...prev,
                        totalElements: res.data.totalElements,
                        totalPages: res.data.totalPages
                    }));

                    if (res.data.content.length === 0 && (searchTerm || mediumTypeFilter || usedByFilter)) {
                        setError('No templates found matching your filters.');
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error("Fetch error:", err);
                    setError('Failed to fetch templates. Please try again.');
                    setTemplateList([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getTemplates();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [pagination.page, pagination.size, mediumTypeFilter, usedByFilter, searchTerm]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({...prev, page: 0}));
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setMediumTypeFilter('');
        setUsedByFilter('');
        setPagination(prev => ({...prev, page: 0}));
    };

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({...prev, page: page - 1}));
    };

    const handleSizeChange = (e) => {
        setPagination(prev => ({...prev, size: Number(e.target.value), page: 0}));
    };

    const getMediumTypeBadge = (mediumType) => {
        const mediumTypeConfig = {
            'EMAIL': { class: 'service-status status-completed text-success', text: 'Email' },
            'SMS': { class: 'service-status status-pending text-info', text: 'SMS' },
            'CALL': { class: 'service-status status-incomplete text-warning', text: 'Call' }
        };

        const config = mediumTypeConfig[mediumType] || { class: 'bg-secondary', text: mediumType };
        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    const getUsedByBadge = (usedBy) => {
        const usedByConfig = {
            'CLINIC': { class: 'bg-success', text: 'Clinic' },
            'SYSTEM': { class: 'bg-warning text-dark', text: 'System' }
        };

        const config = usedByConfig[usedBy] || { class: 'bg-secondary', text: usedBy };
        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    // Calculate the range of items being displayed
    const startItem = pagination.page * pagination.size + 1;
    const endItem = Math.min((pagination.page + 1) * pagination.size, pagination.totalElements);

    // Filter templates based on search and filters
    const filteredTemplates = templateList.filter(template => {
        const matchesSearch = searchTerm === '' ||
            template.reminderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.content.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesMediumType = mediumTypeFilter === '' || template.mediumType === mediumTypeFilter;
        const matchesUsedBy = usedByFilter === '' || template.usedBy === usedByFilter;

        return matchesSearch && matchesMediumType && matchesUsedBy;
    });

    const handleEditClick = (template) => {
        const { id, mediumType } = template;

        switch (mediumType) {
            case 'SMS':
                navigate('edit-sms/' + id);
                break;
            case 'EMAIL':
                navigate('edit-email/' + id);
                break;
            case 'CALL':
                navigate('edit-call/' + id);
                break;
            default:
                console.warn('Unknown medium type:', mediumType);
                // Fallback to a default edit route or show an error
                navigate('edit/' + id);
        }
    };

    return (
        <div className="container-fluid ">
            <div className="card card-padding shadow-sm">
                <div className="break"></div>
                <div className="card-body">
                    {/* Search Filters Section */}
                    <div className="mb-4 p-3 rounded">
                        <form onSubmit={handleSearch}>
                            <div className="row justify-content-end align-items-end">
                                <div className="col-md-5">
                                    <label className="form-label">Medium Type</label>
                                    <select
                                        className="form-control form-select form-control-sm"
                                        value={mediumTypeFilter}
                                        onChange={(e) => setMediumTypeFilter(e.target.value)}
                                    >
                                        <option value="">All Types</option>
                                        <option value="EMAIL">Email</option>
                                        <option value="SMS">SMS</option>
                                        <option value="CALL">Call</option>
                                    </select>
                                </div>

                                <div className="col-md-5">
                                    <label className="form-label">Used By</label>
                                    <select
                                        className="form-control form-select form-control-sm"
                                        value={usedByFilter}
                                        onChange={(e) => setUsedByFilter(e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="CLINIC">Clinic</option>
                                        <option value="SYSTEM">System</option>
                                    </select>
                                </div>

                                <div className="col-md-2 d-flex gap-2">
                                    <button className="btn btn-primary btn-sm" type="submit">
                                        <i className="bi bi-search me-1"></i> Search
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={handleClearFilters}
                                        disabled={!searchTerm && !mediumTypeFilter && !usedByFilter}
                                    >
                                        <i className="bi bi-x"></i> Clear
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    {isLoading ? (
                        <Loading />
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-bordered hover mb-0">
                                    <thead>
                                    <tr>
                                        <th className="col-2"><i className="bi bi-chat-text"></i> Template Name</th>
                                        <th className="col-2"><i className="bi bi-type"></i> Medium Type</th>
                                        <th className="col-4"><i className="bi bi-textarea-t"></i> Content</th>
                                        <th className="col-1"><i className="bi bi-building"></i> Used By</th>
                                        <th className="col-2"><i className="bi bi-calendar"></i> Created At</th>
                                        <th className="col-1"><i className="bi bi-gear"></i> Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredTemplates.length > 0 ? (
                                        filteredTemplates.map(template => (
                                            <React.Fragment key={template.id}>
                                                <tr>
                                                    <td>
                                                        <div className="fw-semibold">{template.reminderName}</div>
                                                    </td>
                                                    <td>
                                                        {getMediumTypeBadge(template.mediumType)}
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{maxWidth: '300px'}} title={template.content}>
                                                            {template.content}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {getUsedByBadge(template.usedBy)}
                                                    </td>
                                                    <td>
                                                        <div className="small">{template.createdAt}</div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => handleEditClick(template)}
                                                                title="Edit"
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => toggleRow(template.id)}
                                                                title="Toggle Details"
                                                                aria-expanded={expandedRows.includes(template.id)}
                                                            >
                                                                {expandedRows.includes(template.id) ? (
                                                                    <i className="bi bi-chevron-up"></i>
                                                                ) : (
                                                                    <i className="bi bi-chevron-down"></i>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" className="p-0 border-top-0">
                                                        <Collapse in={expandedRows.includes(template.id)}>
                                                            <div className="p-3 bg-light">
                                                                <div className="row">
                                                                    <div className="col-md-6">
                                                                        <h6>Template Details</h6>
                                                                        <hr className="my-2"/>
                                                                        <div className="mb-2">
                                                                            <strong>ID:</strong>
                                                                            <span className="text-secondary ms-2 small">{template.id}</span>
                                                                        </div>
                                                                        <div className="mb-2">
                                                                            <strong>Reminder Type ID:</strong>
                                                                            <span className="text-secondary ms-2 small">{template.reminderTypeId}</span>
                                                                        </div>
                                                                        <div className="mb-2">
                                                                            <strong>Full Content:</strong>
                                                                            <div className="text-secondary mt-1 small p-2 bg-white rounded border">
                                                                                {template.content}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <h6>Timestamps</h6>
                                                                        <hr className="my-2"/>
                                                                        <div className="mb-2">
                                                                            <strong>Created:</strong>
                                                                            <span className="text-secondary ms-2 small">{template.createdAt}</span>
                                                                        </div>
                                                                        <div className="mb-2">
                                                                            <strong>Modified:</strong>
                                                                            <span className="text-secondary ms-2 small">{template.modifiedAt}</span>
                                                                        </div>
                                                                        {template.modifiedAt !== template.createdAt && (
                                                                            <div className="alert alert-info small mb-0">
                                                                                <i className="bi bi-info-circle me-1"></i>
                                                                                This template has been modified
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                <div className="alert alert-danger">
                                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                                    {error ? error : "No templates found. Try adjusting your filters."}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            {filteredTemplates.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2">Show:</span>
                                        <select
                                            className="form-select form-select-sm w-auto"
                                            value={pagination.size}
                                            onChange={handleSizeChange}
                                        >
                                            {[10, 25, 50, 100].map(size => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                        <span className="ms-2 text-muted">
                                            Showing {startItem} to {endItem} of {pagination.totalElements} entries
                                        </span>
                                    </div>

                                    <Pagination>
                                        <Pagination.First
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(1)}
                                        />
                                        <Pagination.Prev
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(pagination.page)}
                                        />

                                        {Array.from({length: Math.min(5, pagination.totalPages)}, (_, i) => {
                                            const pageNum = i + 1;
                                            return (
                                                <Pagination.Item
                                                    key={pageNum}
                                                    active={pageNum === pagination.page + 1}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}

                                        <Pagination.Next
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.page + 2)}
                                        />
                                        <Pagination.Last
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.totalPages)}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminTemplateList;